// As-built records (L5), read-only: search by assembly, part number, station,
// technician or date; open one, print it, or download CSV.
import { useState } from 'react';
import { Link } from 'react-router';

import type { RecordFilter } from '../../api/queries';
import { recordsQuery, useRecords } from '../../api/queries';
import { Button, EmptyState, ErrorPanel, Loading, PageHeader, TextField } from '../../design/components';
import { formatDateTime } from '../../design/format';

export function RecordsPage() {
    const [draft, setDraft] = useState({ assembly: '', partNumber: '', station: '', technician: '', from: '', to: '' });
    const [filter, setFilter] = useState<RecordFilter>({ page: 1 });
    const records = useRecords(filter);
    const field = (name: keyof typeof draft) => (value: string) => {
        setDraft({ ...draft, [name]: value });
    };
    const search = () => {
        setFilter({
            ...(draft.assembly ? { assembly: draft.assembly } : {}),
            ...(draft.partNumber ? { partNumber: draft.partNumber } : {}),
            ...(draft.station ? { station: draft.station } : {}),
            ...(draft.technician ? { technician: draft.technician } : {}),
            ...(draft.from ? { from: new Date(`${draft.from}T00:00:00`).toISOString() } : {}),
            ...(draft.to ? { to: new Date(`${draft.to}T23:59:59`).toISOString() } : {}),
            page: 1,
        });
    };
    const page = records.data;
    const pages = page ? Math.max(1, Math.ceil(page.totalCount / page.pageSize)) : 1;
    return (
        <section className="sf-stack" aria-labelledby="records-heading">
            <PageHeader id="records-heading" title="As-built records" />
            <form
                className="sf-card sf-grid"
                role="search"
                onSubmit={(event) => {
                    event.preventDefault();
                    search();
                }}
            >
                <TextField label="Assembly" value={draft.assembly} onChange={field('assembly')} />
                <TextField label="Part number used" value={draft.partNumber} onChange={field('partNumber')} />
                <TextField label="Station" value={draft.station} onChange={field('station')} />
                <TextField label="Technician" value={draft.technician} onChange={field('technician')} />
                <TextField label="Signed off from" type="date" value={draft.from} onChange={field('from')} />
                <TextField label="Signed off to" type="date" value={draft.to} onChange={field('to')} />
                <div className="sf-row">
                    <Button type="submit" variant="primary">
                        Search
                    </Button>
                    <a
                        className="sf-button sf-button--outlined"
                        href={`/api/v1/as-built/csv?${recordsQuery(filter, false)}`}
                        download
                    >
                        Download CSV
                    </a>
                </div>
            </form>
            {records.isPending ? <Loading what="the records" /> : null}
            {records.isError ? <ErrorPanel error={records.error} /> : null}
            {page?.items.length === 0 ? <EmptyState>No signed-off builds match.</EmptyState> : null}
            {page && page.items.length > 0 ? (
                <>
                    <table className="sf-table">
                        <thead>
                            <tr>
                                <th scope="col">Assembly</th>
                                <th scope="col">Station</th>
                                <th scope="col">Quantity</th>
                                <th scope="col">Signed off</th>
                                <th scope="col">Technicians</th>
                            </tr>
                        </thead>
                        <tbody>
                            {page.items.map((record) => (
                                <tr key={record.buildId}>
                                    <td>
                                        <Link to={`/lead/records/${record.buildId}`} className="sf-mono">
                                            {record.assemblyPartNumber}
                                        </Link>{' '}
                                        {record.assemblyName ?? ''}
                                    </td>
                                    <td>{record.station}</td>
                                    <td>{record.buildQuantity}</td>
                                    <td>
                                        {formatDateTime(record.signedOffAt)}
                                        <div className="sf-muted">{record.signedOffBy}</div>
                                    </td>
                                    <td>{record.technicians.join(', ')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <nav className="sf-row" aria-label="Pages">
                        <Button
                            disabled={page.page <= 1}
                            onClick={() => {
                                setFilter({ ...filter, page: page.page - 1 });
                            }}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {page.page} of {pages} ({page.totalCount} records)
                        </span>
                        <Button
                            disabled={page.page >= pages}
                            onClick={() => {
                                setFilter({ ...filter, page: page.page + 1 });
                            }}
                        >
                            Next
                        </Button>
                    </nav>
                </>
            ) : null}
        </section>
    );
}
